/**
 * TabelaDeLeituras — os mesmos dados do gráfico, em tabela.
 *
 * POR QUE ELA TEM ESCADA PRÓPRIA DE GRANULARIDADE. O gráfico mira 300–500
 * pontos, que é ótimo pra desenhar e ilegível pra ler: 24 h no bucket do
 * gráfico dá 288 linhas. A tabela usa uma escada mais grossa — 1 h até 7
 * dias — e por isso mostra agregados de janelas DIFERENTES das do gráfico
 * que está logo acima.
 *
 * Isso é decisão, não descuido, e a defesa contra a confusão é o cabeçalho
 * dizer o que uma linha é ("média por hora", não "média") e o seletor de
 * granularidade ficar à vista. Sem isso alguém compara um número com o
 * gráfico, vê diferença e conclui que o sistema erra.
 *
 * A COLUNA `máx` NÃO É ENFEITE. O endpoint consolida todos os devices do
 * papel: com duas sondas na mesma câmara, a média da linha mistura as
 * duas. Sondas a 12 e a 2 °C dão média 7 — dentro de uma faixa 0–8. É a
 * mesma armadilha que a gente recusou no motor de alerta. Aqui a média
 * pode ficar porque `mín` e `máx` estão ao lado; sozinha, não poderia.
 */

import { useMemo, useState } from "react";
import { Download, Table2 } from "lucide-react";

import { Button } from "./Button";
import { Field, Select } from "./Field";
import { DataTable, type Column } from "./DataTable";
import type { Reading } from "@/types";

/** Bucket do backend → rótulo do que uma linha representa. */
export const GRANULARIDADES = [
  { value: "15 minutes", label: "15 minutos", porLinha: "média por 15 min" },
  { value: "1 hour",     label: "1 hora",     porLinha: "média por hora" },
  { value: "6 hours",    label: "6 horas",    porLinha: "média por 6 h" },
  { value: "1 day",      label: "1 dia",      porLinha: "média diária" },
] as const;

/**
 * Escada da TABELA — deliberadamente mais grossa que `bucketForSpanHours`
 * do gráfico. O alvo aqui é ~24 a 200 linhas, não 500 pontos.
 */
export function granularidadeParaJanela(horas: number): string {
  if (horas <= 12)      return "15 minutes";   // até 48 linhas
  if (horas <= 24 * 7)  return "1 hour";       // até 168 linhas
  if (horas <= 24 * 60) return "6 hours";      // até 240 linhas
  return "1 day";
}

interface Serie {
  rotulo: string;
  unidade: string;
  dados: Reading[] | undefined;
}

interface Props {
  series: Serie[];
  granularidade: string;
  onGranularidade: (v: string) => void;
  carregando?: boolean;
  /** Nome usado no arquivo exportado. */
  nomeBase?: string;
}

interface Linha {
  time: string;
  [coluna: string]: string | number | null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function fmt(v: number | null, casas = 1): string {
  return v === null ? "—" : v.toFixed(casas);
}

function quando(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

export function TabelaDeLeituras({
  series, granularidade, onGranularidade, carregando, nomeBase = "leituras",
}: Props) {
  const [aberta, setAberta] = useState(false);

  const comDados = series.filter((s) => (s.dados?.length ?? 0) > 0);

  /** Junta as séries pela marca de tempo — o bucket é o mesmo pra todas. */
  const linhas = useMemo<Linha[]>(() => {
    const porTempo = new Map<string, Linha>();
    for (const s of comDados) {
      for (const r of s.dados ?? []) {
        const atual = porTempo.get(r.time) ?? { time: r.time };
        atual[`${s.rotulo}:media`] = num(r.value);
        atual[`${s.rotulo}:min`] = num(r.min);
        atual[`${s.rotulo}:max`] = num(r.max);
        porTempo.set(r.time, atual);
      }
    }
    return [...porTempo.values()].sort((a, b) => b.time.localeCompare(a.time));
  }, [comDados]);

  const rotuloDaLinha =
    GRANULARIDADES.find((g) => g.value === granularidade)?.porLinha ?? "média";

  const columns = useMemo<Column<Linha>[]>(() => {
    const cols: Column<Linha>[] = [{
      key: "time",
      header: "Quando",
      cell: (r) => <span className="whitespace-nowrap text-sm">{quando(r.time)}</span>,
    }];
    for (const s of comDados) {
      for (const [sufixo, titulo, casas] of [
        ["media", "méd", 1], ["min", "mín", 1], ["max", "máx", 1],
      ] as const) {
        cols.push({
          key: `${s.rotulo}:${sufixo}`,
          header: `${s.rotulo} ${titulo}`,
          cell: (r) => (
            <span className="tabular-nums text-sm">
              {fmt(r[`${s.rotulo}:${sufixo}`] as number | null, casas)}
            </span>
          ),
        });
      }
    }
    return cols;
  }, [comDados]);

  function exportarCsv() {
    // Ponto-e-vírgula e vírgula decimal: é o que o Excel em pt-BR abre sem
    // pedir assistente de importação. CSV "correto" que abre tudo numa
    // coluna só não é correto pra quem vai usar.
    const cabecalho = ["Quando", ...comDados.flatMap((s) => [
      `${s.rotulo} média (${s.unidade})`,
      `${s.rotulo} mín (${s.unidade})`,
      `${s.rotulo} máx (${s.unidade})`,
    ])];
    const corpo = linhas.map((l) => [
      quando(l.time),
      ...comDados.flatMap((s) => ["media", "min", "max"].map((sufixo) => {
        const v = l[`${s.rotulo}:${sufixo}`] as number | null;
        return v === null ? "" : v.toFixed(1).replace(".", ",");
      })),
    ]);
    const texto = [cabecalho, ...corpo].map((l) => l.join(";")).join("\r\n");
    // BOM: sem ele o Excel lê UTF-8 como Latin-1 e "média" vira "mÃ©dia".
    const blob = new Blob(["﻿" + texto], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${nomeBase}-${granularidade.replace(/\s+/g, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!aberta) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setAberta(true)}>
        <Table2 className="mr-1.5 h-4 w-4" />
        Ver dados em tabela
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label="Agrupar por">
          <Select
            value={granularidade}
            onChange={(e) => onGranularidade(e.target.value)}
            options={GRANULARIDADES.map((g) => ({ value: g.value, label: g.label }))}
          />
        </Field>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={exportarCsv} disabled={!linhas.length}>
            <Download className="mr-1.5 h-4 w-4" />
            CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAberta(false)}>
            Ocultar
          </Button>
        </div>
      </div>

      {/* O que uma linha É. Sem isto, alguém compara um valor com o gráfico
          acima — que usa bucket mais fino — e conclui que o sistema erra. */}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Cada linha é a <strong>{rotuloDaLinha}</strong> do período.
        As colunas <strong>mín</strong> e <strong>máx</strong> são os extremos
        dentro da janela — e, quando o ativo tem mais de um sensor no mesmo
        papel, também entre os sensores. O gráfico acima usa janela mais
        curta, então os números não se correspondem linha a ponto.
      </p>

      <DataTable
        data={linhas}
        columns={columns}
        loading={carregando}
        rowKey={(r) => r.time}
        emptyTitle="Sem leituras no período"
      />
    </div>
  );
}
